import React, { useEffect, PropsWithChildren, useRef } from "react";
import { Droppable, DroppableProps } from "@hello-pangea/dnd";
import { cn } from "../../styles";
import { useDragContext } from "../../util/DragProvider";
import { DragListItem } from "./DragListItem";

export type { DragListItemProps } from "./DragListItem";

/**
 * Type of change event that occurred in the drag list.
 * - "reorder": Item was moved within the same list
 * - "remove": Item was moved out of the list
 * - "add": Item was added to the list from another source
 */
export type DragListChangeType = "reorder" | "remove" | "add";

/**
 * Event object passed to the onDragListChange callback when a drag operation completes.
 * Contains all information about the drag operation including source and destination.
 */
export interface DragListChangeEvent<T = unknown> {
  /**
   * The type of change that occurred (reorder, remove, or add).
   */
  type: DragListChangeType;
  /**
   * The index position of the item in the source list.
   */
  sourceIndex: number;
  /**
   * The index position where the item was dropped in the destination list.
   */
  destinationIndex: number;
  /**
   * The data associated with the dragged item.
   */
  item: T;
  /**
   * The ID of the source list.
   */
  sourceId: string;
  /**
   * The ID of the destination list.
   */
  destinationId: string;
}

export interface DragListProps<T = unknown> extends PropsWithChildren {
  /**
   * Unique identifier for the drag list.
   * Used to track drag operations between multiple lists.
   */
  id: string;
  /**
   * Optional type identifier for the droppable area.
   * Items can only be dropped in areas with matching types.
   * @default id
   */
  type?: string;
  /**
   * Additional CSS class name(s) for the list container.
   */
  className?: string;
  /**
   * CSS class name(s) applied when any item is being dragged.
   * Useful for global drag state styling.
   */
  draggingClassName?: string;
  /**
   * CSS class name(s) applied when an item is hovering over this list.
   * Provides visual feedback during drag operations.
   */
  hoverClassName?: string;
  /**
   * Direction of the drag list layout.
   * @default "vertical"
   */
  direction?: DroppableProps["direction"];
  /**
   * Callback function called when a drag operation completes.
   * Receives an event object with full details about the drag operation.
   */
  onDragListChange: (event: DragListChangeEvent<T>) => void;
}

/**
 * Drag and drop list container built on @hello-pangea/dnd.
 * Provides a droppable area for draggable items with support for reordering,
 * moving items between lists, and custom styling during drag operations.
 * Use with DragList.Item for individual draggable items.
 */
export function DragList<T = unknown>({
  id,
  type,
  children,
  className,
  onDragListChange,
  draggingClassName,
  hoverClassName,
  direction = "vertical",
}: DragListProps<T>) {
  const { registerReorderCallback, isDragging } = useDragContext();
  const itemsRef = useRef<Map<number, { data?: T; children: React.ReactNode }>>(
    new Map()
  );

  useEffect(() => {
    return registerReorderCallback(id, onDragListChange);
  }, [id, onDragListChange, registerReorderCallback]);

  return (
    <Droppable droppableId={id} type={type || id} direction={direction}>
      {(provided, snapshot) => (
        <div
          {...provided.droppableProps}
          ref={provided.innerRef}
          className={cn(
            className,
            isDragging && draggingClassName,
            snapshot.isDraggingOver && hoverClassName
          )}
        >
          {children}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
}

DragList.displayName = "DragList";

DragList.Item = DragListItem;
